<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  assistantId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainInboxes/getUIFlags'),
  inboxes: useMapGetter('inboxes/getInboxes'),
  captainInboxes: useMapGetter('captainInboxes/getRecords'),
  assistants: useMapGetter('captainAssistants/getRecords'),
};

const state = reactive({ inboxIds: [] });

const validationRules = {
  inboxIds: { required },
};

// Which other assistant owns each inbox. An inbox belongs to at most one
// assistant, so a flat map is enough. The current assistant is excluded: its
// own inboxes are filtered out of the list entirely rather than shown disabled.
const ownerByInboxId = computed(() => {
  const currentId = Number(props.assistantId);
  const owners = {};

  formState.assistants.value.forEach(assistant => {
    if (assistant.id === currentId) return;
    (assistant.inbox_ids || []).forEach(inboxId => {
      owners[inboxId] = assistant.name;
    });
  });

  return owners;
});

const inboxList = computed(() => {
  const connectedIds = formState.captainInboxes.value.map(inbox => inbox.id);

  return formState.inboxes.value
    .filter(inbox => !connectedIds.includes(inbox.id))
    .map(inbox => {
      const owner = ownerByInboxId.value[inbox.id];
      return {
        value: inbox.id,
        label: inbox.name,
        disabled: Boolean(owner),
        description: owner
          ? t('CAPTAIN.INBOXES.FORM.INBOX.CONNECTED_TO', { assistant: owner })
          : '',
      };
    });
});

const availableInboxIds = computed(() =>
  inboxList.value.filter(option => !option.disabled).map(option => option.value)
);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const formErrors = computed(() => ({
  inboxIds: v$.value.inboxIds.$error
    ? t('CAPTAIN.INBOXES.FORM.INBOX.ERROR')
    : '',
}));

const hasSelectedEverything = computed(
  () => state.inboxIds.length === availableInboxIds.value.length
);

const handleToggleAll = () => {
  state.inboxIds = hasSelectedEverything.value
    ? []
    : [...availableInboxIds.value];
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', {
    assistantId: props.assistantId,
    inboxIds: state.inboxIds,
  });
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between mb-0.5">
        <label for="inbox" class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.INBOXES.FORM.INBOX.LABEL') }}
        </label>
        <Button
          v-if="availableInboxIds.length"
          type="button"
          variant="link"
          size="xs"
          :label="
            hasSelectedEverything
              ? t('CAPTAIN.INBOXES.FORM.INBOX.CLEAR_ALL')
              : t('CAPTAIN.INBOXES.FORM.INBOX.SELECT_ALL')
          "
          @click="handleToggleAll"
        />
      </div>
      <TagMultiSelectComboBox
        id="inbox"
        v-model="state.inboxIds"
        :options="inboxList"
        :has-error="!!formErrors.inboxIds"
        :placeholder="t('CAPTAIN.INBOXES.FORM.INBOX.PLACEHOLDER')"
        :search-placeholder="t('CAPTAIN.INBOXES.FORM.INBOX.SEARCH_PLACEHOLDER')"
        :empty-state="t('CAPTAIN.INBOXES.FORM.INBOX.EMPTY_STATE')"
        :message="formErrors.inboxIds"
      />
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-violet-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
